import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { enumUtil } from "../../../utils/Enum";
import { ServerRequestProtected, ServerRequestPublic } from "../../../utils/ServerDataRequest";
import NotificationHandler from "../../../utils/NotificationHandler";
import Loader from "../Loader/Loader";
import DropdownValueHandler from "../SuperSearch/DropdownValueHandler";
import States, { CoveragePriority, GenderDemographic, GenderIdentity, Relationship } from "../../../utils/Constants";
import isNull from "../../../utils/NullChecking";
import { formatFax } from "../../../utils/Formatting";
import FormatPhoneNumber from "../../../utils/FormatPhoneNumber";
import File from "../../EHR/Intake Form/itemsPreview/File";
import ImagePreview from "../../EHR/Intake Form/itemsPreview/ImagePreview";

const MergeInsuranceForm = ({ mergeInfoFormInsurance, onClose, ID }) => {
    const [isLoading, setIsLoading] = useState(false)
    const API_URL = enumUtil.controller.Patient
    const GENERAL_API_URL = enumUtil.controller.General || "General"
    const [Data, setData] = useState(null)

    const LoadData = () => {
        setIsLoading(true)
        ServerRequestProtected(API_URL + "/GetPatientIntakeformData", 'post', {
            "TableName": enumUtil.enumAPITableName.PatientInsuranceTemplateData,
            "ID": ID
        }).then((response) => {
            setIsLoading(false)
            setData(response)
        }).catch((error) => {
            setIsLoading(false)
            NotificationHandler(error.response?.data || "Error loading insurance data", enumUtil.enumtoaster.error)
            setData(null)
        })
    }

    useEffect(() => {
        if (ID) {
            LoadData()
        }
    }, [ID])

    // Helper function to get field value (handles typos in field names)
    const getField = (fieldName, isPatient = false) => {
        if (!Data) return null;

        // Handle field name variations and typos
        const fieldMap = {
            // Submitted form fields
            'PrimaryGroupName': Data.PrimaryGroupName || Data.PriamryGroupName,
            'PrimaryRelationship': Data.PrimaryRelationship || Data.PriamryRelationShipCode,
            'PrimaryFirstName': Data.PrimaryFirstName || Data.PriamryFirstName,
            'PrimaryLastName': Data.PrimaryLastName || Data.PriamryLastName,
            'PrimaryMiddleName': Data.PrimaryMiddleName || Data.PriamryMiddleName,
            'PrimaryDOB': Data.PrimaryDOB || Data.PriamryDOB,
            'PrimarySex': Data.PrimarySex || Data.PriamrySex,
            'PrimarySSN': Data.PrimarySSN || Data.PriamrySSN,
            'PrimaryAddress1': Data.PrimaryAddress1 || Data.PriamryAddress1,
            'PrimaryAddress2': Data.PrimaryAddress2 || Data.PriamryAddress2,
            'PrimaryCity': Data.PrimaryCity || Data.PriamryCity,
            'PrimaryState': Data.PrimaryState || Data.PriamryState,
            'PrimaryZipCode': Data.PrimaryZipCode || Data.PriamryZipCode,
            'PrimaryInsuranceCardFront': Data.PrimaryInsuranceCardFront || Data.PriamryInsuranceCardFront,
            'PrimaryInsuranceCardBack': Data.PrimaryInsuranceCardBack || Data.PriamryInsuranceCardBack,
            'PrimaryInsuranceCardFrontFileName': Data.PrimaryInsuranceCardFrontFileName || Data.PriamryInsuranceCardFrontFileName,
            'PrimaryInsuranceCardBackFileName': Data.PrimaryInsuranceCardBackFileName || Data.PriamryInsuranceCardBackFileName,

            // Patient fields with typos
            'PatientPrimaryState': Data.PatientPrimaryState || Data.PatientPrimamryState,
            'PatientPrimaryInsuranceCardFront': Data.PatientPrimaryInsuranceCardFront || Data.PatientPriamryInsuranceCardFront,
            'PatientPrimaryInsuranceCardBack': Data.PatientPrimaryInsuranceCardBack || Data.PatientPriamryInsuranceCardBack,
        };

        const prefixedField = isPatient ? `Patient${fieldName}` : fieldName;

        // First check the field map for typos
        if (fieldMap[prefixedField] !== undefined) {
            return fieldMap[prefixedField];
        }

        // Then check the original field
        return Data[prefixedField];
    };

    // Helper function to get identifier field
    const getIdentifier = (fieldCode) => {
        if (!Data) return 'false';
        return Data[`${fieldCode}Identifier`] || 'false';
    };

    const ReconcileSingle = (fieldName) => {
        if (!Data) return

        // Create the request data according to the API structure
        const requestData = {
            "TableName": "PatientInsuranceTemplateData",
            "ID": Data.PatientID.toString(),
            "Data": {
                "FormSelfPay": Data.FormSelfPay || "false",
                "PatientPrimaryInsuranceID": Data.PatientPrimaryInsuranceID + "" || "",
                "PrimaryInsuranceID": Data.PrimaryInsuranceID + "" || "",
                "PrimaryRelationShipCode": "",
                "PrimaryLastName": "",
                "PrimaryFirstName": "",
                "PrimaryMiddleName": "",
                "PrimarySex": "",
                "PrimaryDOB": "",
                "PrimarySSN": "",
                "PrimaryAddress1": "",
                "PrimaryAddress2": "",
                "PrimaryCity": "",
                "PrimaryState": "",
                "PrimaryZipCode": "",
                "PrimaryGroupName": "",
                "PrimaryGroupNumber": "",
                "PrimarySubscriberID": "",
                "PrimaryInsuranceCardFront": "",
                "PrimaryInsuranceCardBack": "",

                "PatientSecondaryInsuranceID": Data.PatientSecondaryInsuranceID + "" || "",
                "SecondaryInsuranceID": Data.SecondaryInsuranceID + "" || "",
                "SecondaryRelationShipCode": "",
                "SecondaryLastName": "",
                "SecondaryFirstName": "",
                "SecondaryMiddleName": "",
                "SecondarySex": "",
                "SecondaryDOB": "",
                "SecondarySSN": "",
                "SecondaryAddress1": "",
                "SecondaryAddress2": "",
                "SecondaryCity": "",
                "SecondaryState": "",
                "SecondaryZipCode": "",
                "SecondaryGroupName": "",
                "SecondaryGroupNumber": "",
                "SecondarySubscriberID": "",
                "SecondaryInsuranceCardFront": "",
                "SecondaryInsuranceCardBack": ""
            }
        };

        // Map field names to the API structure
        const fieldMapping = {
            // Primary Insurance fields
            'PrimarySubscriberID': 'PrimarySubscriberID',
            'PrimaryGroupNumber': 'PrimaryGroupNumber',
            'PrimaryGroupName': 'PrimaryGroupName',
            'PrimaryRelationship': 'PrimaryRelationShipCode',
            'PrimaryFirstName': 'PrimaryFirstName',
            'PrimaryLastName': 'PrimaryLastName',
            'PrimaryMiddleName': 'PrimaryMiddleName',
            'PrimaryDOB': 'PrimaryDOB',
            'PrimarySex': 'PrimarySex',
            'PrimarySSN': 'PrimarySSN',
            'PrimaryAddress1': 'PrimaryAddress1',
            'PrimaryAddress2': 'PrimaryAddress2',
            'PrimaryCity': 'PrimaryCity',
            'PrimaryState': 'PrimaryState',
            'PrimaryZipCode': 'PrimaryZipCode',
            'PrimaryInsuranceCardFront': 'PrimaryInsuranceCardFront',
            'PrimaryInsuranceCardBack': 'PrimaryInsuranceCardBack',

            // Secondary Insurance fields
            'SecondarySubscriberID': 'SecondarySubscriberID',
            'SecondaryGroupNumber': 'SecondaryGroupNumber',
            'SecondaryGroupName': 'SecondaryGroupName',
            'SecondaryRelationship': 'SecondaryRelationShipCode',
            'SecondaryFirstName': 'SecondaryFirstName',
            'SecondaryLastName': 'SecondaryLastName',
            'SecondaryMiddleName': 'SecondaryMiddleName',
            'SecondaryDOB': 'SecondaryDOB',
            'SecondarySex': 'SecondarySex',
            'SecondarySSN': 'SecondarySSN',
            'SecondaryAddress1': 'SecondaryAddress1',
            'SecondaryAddress2': 'SecondaryAddress2',
            'SecondaryCity': 'SecondaryCity',
            'SecondaryState': 'SecondaryState',
            'SecondaryZipCode': 'SecondaryZipCode',
            'SecondaryInsuranceCardFront': 'SecondaryInsuranceCardFront',
            'SecondaryInsuranceCardBack': 'SecondaryInsuranceCardBack'
        };

        const apiFieldName = fieldMapping[fieldName];
        if (!apiFieldName) return;

        // Set the field value
        const fieldValue = getField(fieldName);
        requestData.Data[apiFieldName] = fieldValue || '';

        // Set the InsuranceID if we're merging SubscriberID
        if (fieldName === 'PrimarySubscriberID' || fieldName === 'SecondarySubscriberID') {
            if (fieldName === 'PrimarySubscriberID') {
                requestData.Data.PrimaryInsuranceID = Data.PrimaryInsuranceID || "";
            } else if (fieldName === 'SecondarySubscriberID') {
                requestData.Data.SecondaryInsuranceID = Data.SecondaryInsuranceID || "";
            }
        }
        requestData.Data['PatientIntakeFormID'] = Data.PatientIntakeFormID + "";
        setIsLoading(true)
        ServerRequestProtected(API_URL + "/MergePatientInsurance", 'post', requestData)
            .then((response) => {
                setIsLoading(false)
                NotificationHandler("Insurance field updated successfully", enumUtil.enumtoaster.success)

                // Reload data to get latest patient data
                LoadData()
            })
            .catch((error) => {
                setIsLoading(false)
                NotificationHandler(error.response?.data || "Error updating insurance field", enumUtil.enumtoaster.error)
            })
    }

    const ReconcileAll = () => {
        if (!Data) return

        // // Create the request data according to the API structure
        // const requestData = {
        //     "TableName": "PatientInsuranceTemplateData",
        //     "ID": Data.PatientID.toString(),
        //     "Data": {
        //         "FormSelfPay": Data.FormSelfPay ? "true" : "false",
        //         "PatientPrimaryInsuranceID": Data.PatientPrimaryInsuranceID + "" || "",
        //         "PrimaryInsuranceID": Data.PrimaryInsuranceID ? Data.PrimaryInsuranceID.toString() : "",
        //         "PrimaryRelationShipCode": "",
        //         "PrimaryLastName": "",
        //         "PrimaryFirstName": "",
        //         "PrimaryMiddleName": "",
        //         "PrimarySex": "",
        //         "PrimaryDOB": "",
        //         "PrimarySSN": "",
        //         "PrimaryAddress1": "",
        //         "PrimaryAddress2": "",
        //         "PrimaryCity": "",
        //         "PrimaryState": "",
        //         "PrimaryZipCode": "",
        //         "PrimaryGroupName": "",
        //         "PrimaryGroupNumber": "",
        //         "PrimarySubscriberID": "",
        //         "PrimaryInsuranceCardFront": "",
        //         "PrimaryInsuranceCardBack": "",

        //         "PatientSecondaryInsuranceID": Data.PatientSecondaryInsuranceID + "" || "",
        //         "SecondaryInsuranceID": Data.SecondaryInsuranceID ? Data.SecondaryInsuranceID.toString() : "",
        //         "SecondaryRelationShipCode": "",
        //         "SecondaryLastName": "",
        //         "SecondaryFirstName": "",
        //         "SecondaryMiddleName": "",
        //         "SecondarySex": "",
        //         "SecondaryDOB": "",
        //         "SecondarySSN": "",
        //         "SecondaryAddress1": "",
        //         "SecondaryAddress2": "",
        //         "SecondaryCity": "",
        //         "SecondaryState": "",
        //         "SecondaryZipCode": "",
        //         "SecondaryGroupName": "",
        //         "SecondaryGroupNumber": "",
        //         "SecondarySubscriberID": "",
        //         "SecondaryInsuranceCardFront": "",
        //         "SecondaryInsuranceCardBack": ""
        //     }
        // };

        // let hasChanges = false;

        // // Map identifier codes to field names
        // const identifierMapping = {
        //     // Primary Insurance identifiers
        //     'P_GroupNumberIdentifier': { field: 'PrimaryGroupNumber', apiField: 'PrimaryGroupNumber', value: Data.PrimaryGroupNumber },
        //     'P_InsuranceIDIdentifier': { field: 'PrimaryInsuranceID', apiField: 'PrimaryInsuranceID', value: Data.PrimaryInsuranceID },
        //     'P_InsuranceNameIdentifier': { field: 'PrimaryInsuranceName', apiField: 'PrimaryInsuranceName', value: Data.PrimaryInsuranceName },
        //     'P_SubscriberIDIdentifier': { field: 'PrimarySubscriberID', apiField: 'PrimarySubscriberID', value: Data.PrimarySubscriberID },
        //     'P_GroupNameIdentifier': { field: 'PrimaryGroupName', apiField: 'PrimaryGroupName', value: getField('PrimaryGroupName') },
        //     'P_RelationshipIdentifier': { field: 'PrimaryRelationship', apiField: 'PrimaryRelationShipCode', value: getField('PrimaryRelationship') },
        //     'P_FirstNameIdentifier': { field: 'PrimaryFirstName', apiField: 'PrimaryFirstName', value: getField('PrimaryFirstName') },
        //     'P_LastNameIdentifier': { field: 'PrimaryLastName', apiField: 'PrimaryLastName', value: getField('PrimaryLastName') },
        //     'P_MiddleNameIdentifier': { field: 'PrimaryMiddleName', apiField: 'PrimaryMiddleName', value: getField('PrimaryMiddleName') },
        //     'P_DOBIdentifier': { field: 'PrimaryDOB', apiField: 'PrimaryDOB', value: getField('PrimaryDOB') },
        //     'P_GenderIdentifier': { field: 'PrimarySex', apiField: 'PrimarySex', value: getField('PrimarySex') },
        //     'P_SSNIdentifier': { field: 'PrimarySSN', apiField: 'PrimarySSN', value: getField('PrimarySSN') },
        //     'P_Address1Identifier': { field: 'PrimaryAddress1', apiField: 'PrimaryAddress1', value: getField('PrimaryAddress1') },
        //     'P_Address2Identifier': { field: 'PrimaryAddress2', apiField: 'PrimaryAddress2', value: getField('PrimaryAddress2') },
        //     'P_CityIdentifier': { field: 'PrimaryCity', apiField: 'PrimaryCity', value: getField('PrimaryCity') },
        //     'P_StateIdentifier': { field: 'PrimaryState', apiField: 'PrimaryState', value: getField('PrimaryState') },
        //     'P_ZipIdentifier': { field: 'PrimaryZipCode', apiField: 'PrimaryZipCode', value: getField('PrimaryZipCode') },
        //     // 'P_CardFrontIdentifier': { field: 'PrimaryInsuranceCardFront', apiField: 'PrimaryInsuranceCardFront', value: getField('PrimaryInsuranceCardFront') },
        //     // 'P_CardBackIdentifier': { field: 'PrimaryInsuranceCardBack', apiField: 'PrimaryInsuranceCardBack', value: getField('PrimaryInsuranceCardBack') },

        //     // Secondary Insurance identifiers
        //     'S_GroupNumberIdentifier': { field: 'SecondaryGroupNumber', apiField: 'SecondaryGroupNumber', value: Data.SecondaryGroupNumber },
        //     'S_InsuranceIDIdentifier': { field: 'SecondaryInsuranceID', apiField: 'SecondaryInsuranceID', value: Data.SecondaryInsuranceID + "" },
        //     'S_InsuranceNameIdentifier': { field: 'SecondaryInsuranceName', apiField: 'SecondaryInsuranceName', value: Data.SecondaryInsuranceName },
        //     'S_SubscriberIDIdentifier': { field: 'SecondarySubscriberID', apiField: 'SecondarySubscriberID', value: Data.SecondarySubscriberID },
        //     'S_GroupNameIdentifier': { field: 'SecondaryGroupName', apiField: 'SecondaryGroupName', value: Data.SecondaryGroupName },
        //     'S_RelationshipIdentifier': { field: 'SecondaryRelationship', apiField: 'SecondaryRelationShipCode', value: Data.SecondaryRelationShipCode },
        //     'S_FirstNameIdentifier': { field: 'SecondaryFirstName', apiField: 'SecondaryFirstName', value: Data.SecondaryFirstName },
        //     'S_LastNameIdentifier': { field: 'SecondaryLastName', apiField: 'SecondaryLastName', value: Data.SecondaryLastName },
        //     'S_MiddleNameIdentifier': { field: 'SecondaryMiddleName', apiField: 'SecondaryMiddleName', value: Data.SecondaryMiddleName },
        //     'S_DOBIdentifier': { field: 'SecondaryDOB', apiField: 'SecondaryDOB', value: Data.SecondaryDOB },
        //     'S_GenderIdentifier': { field: 'SecondarySex', apiField: 'SecondarySex', value: Data.SecondarySex },
        //     'S_SSNIdentifier': { field: 'SecondarySSN', apiField: 'SecondarySSN', value: Data.SecondarySSN },
        //     'S_Address1Identifier': { field: 'SecondaryAddress1', apiField: 'SecondaryAddress1', value: Data.SecondaryAddress1 },
        //     'S_Address2Identifier': { field: 'SecondaryAddress2', apiField: 'SecondaryAddress2', value: Data.SecondaryAddress2 },
        //     'S_CityIdentifier': { field: 'SecondaryCity', apiField: 'SecondaryCity', value: Data.SecondaryCity },
        //     'S_StateIdentifier': { field: 'SecondaryState', apiField: 'SecondaryState', value: Data.SecondaryState },
        //     'S_ZipIdentifier': { field: 'SecondaryZipCode', apiField: 'SecondaryZipCode', value: Data.SecondaryZipCode },
        //     // 'S_CardFrontIdentifier': { field: 'SecondaryInsuranceCardFront', apiField: 'SecondaryInsuranceCardFront', value: Data.SecondaryInsuranceCardFront },
        //     // 'S_CardBackIdentifier': { field: 'SecondaryInsuranceCardBack', apiField: 'SecondaryInsuranceCardBack', value: Data.SecondaryInsuranceCardBack }
        // };

        // // Add only fields that have changes (Identifier is true)
        // Object.keys(identifierMapping).forEach(identifierCode => {
        //     const identifier = Data[identifierCode];

        //     if (identifier === 'true') {
        //         const mapping = identifierMapping[identifierCode];
        //         if (mapping && mapping.value !== undefined && mapping.value !== null) {
        //             requestData.Data[mapping.apiField] = mapping.value || '';
        //             hasChanges = true;
        //         }
        //     }
        // });

        // // Check if there are any fields to update
        // if (!hasChanges) {
        //     NotificationHandler("No insurance fields to reconcile", enumUtil.enumtoaster.info)
        //     return
        // }

        // console.log("Reconcile All Request Data:", requestData); // For debugging

        setIsLoading(true)
        ServerRequestProtected(API_URL + "/MergePatientInsurance", 'post', {
            "TableName": "PatientInsuranceTemplateData",
            "ID": ID + "",
        }).then((response) => {
            setIsLoading(false)
            NotificationHandler("All insurance fields reconciled successfully", enumUtil.enumtoaster.success)

            // Reload data to get latest patient data
            LoadData()
        }).catch((error) => {
            setIsLoading(false)
            NotificationHandler(error.response?.data || "Error reconciling insurance fields", enumUtil.enumtoaster.error)
        })
    }

    const MarkAsProcessed = () => {
        // Add API call here when available
        setIsLoading(true)
        ServerRequestProtected(API_URL + "/UpdateIntakeStatus", 'post', {
            "TableName": enumUtil.enumAPITableName.PatientIntakeForm,
            "ID": ID,
            "Data": {
                "IntakeStatus": "Processed"
            }
        }).then((response) => {
            setIsLoading(false)
            setData(response)
            NotificationHandler('Record Saved Successfully!', enumUtil.enumtoaster.success)
            onClose()
        }).catch((error) => {
            setIsLoading(false)
            NotificationHandler(error.response?.data || "Error loading insurance data", enumUtil.enumtoaster.error)
        })
    }

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });
        } catch (error) {
            return dateString;
        }
    };
    const [imgFile, setImgFile] = useState(null)
    const API_URL_Wasabi = enumUtil.controller.Wasabi
    const getSignaturePreview = (SignaturePath) => {
        if (!isNull(SignaturePath)) {
            setImgFile(null);
            ServerRequestPublic(API_URL_Wasabi + "/DownloadFile",
                "post", { BucketPath: SignaturePath, }, "Download")
                .then((response) => {
                    if (response.type === "application/txt") {
                    } else if (["application/png", "application/jpg", "application/jpeg", "application/tiff"].includes(response.type)) {
                        const blob = response;
                        const objectURL = URL.createObjectURL(blob);
                        setImgFile(objectURL);
                    } else if (response.type === "application/pdf") {
                    } else {
                    }
                })
                .catch((error) => {
                    NotificationHandler("File Not Found", enumUtil.enumtoaster.error);
                    setImgFile(null)
                });
        }
    }
    useEffect(() => {
        if (Data && Data?.PatientSignature) {
            getSignaturePreview(Data.PatientSignature)
        }
    }, [Data])

    return (
        <Modal show={mergeInfoFormInsurance} onHide={onClose} backdrop="static" keyboard={false} centered dialogClassName="ar_merg-info-form-popup">
            {isLoading ? <Loader /> : null}
            <Modal.Body>
                <div className="d-flex align-items-center justify-content-center gap-2 mb-12">
                    <i className="icon-Icon-metro-cross fs-20 cursor-pointer" onClick={onClose}></i>
                </div>
                <div className="ar_native_pdf_design">
                    <div className='d-flex flex-column gap-10'>
                        <div className="d-flex align-items-center justify-content-between pb-1-5 br-bottom-1 za_br_light">
                            <p className="text fs-11 ar-light-color">Submitted on {Data?.SubmittedDate}</p>
                            <div className="d-flex align-items-center gap-6">
                                {Data?.Status === 'Processed' ? null :
                                    <p className="text validate-color font-m pe-1-5 br-right-1 br-clr-light cursor-pointer" onClick={MarkAsProcessed}>Mark As Processed</p>
                                }
                                {Data?.TrueIdentifierCount > 0 ?
                                    <p className="text validate-color font-m cursor-pointer" onClick={ReconcileAll}>Reconcile All</p>
                                    : null}
                            </div>
                        </div>

                        <div className="d-flex align-items-center justify-content-center pat-det-header">
                            <p className="heading text-white">Patient Insurance</p>
                        </div>

                        <div className="d-flex align-items-center justify-content-between">
                            <div className="">
                                <img src={Data?.PracticeImage} alt="" style={{ maxHeight: '60px' }} crossOrigin="anonymous" />
                            </div>
                            <div className="text-center">
                                <p className='heading validate-color fs-14'>{Data?.PracticeName}</p>
                                <p className="text text-black">
                                    {Data?.PracticeAddress} <br />
                                    {Data?.PracticeCity || ''}
                                    {Data?.PracticeCity && Data?.PracticeState ? ', ' : ''}
                                    {Data?.PracticeState || ''}
                                    {Data?.PracticeState && Data?.PracticeZipCode ? ', ' : ''}
                                    {Data?.PracticeZipCode || ''}
                                </p>
                            </div>
                            <div className="d-flex gap-10">
                                <div className="">
                                    <p className='text text-black font-m pb-2px'>Tel:</p>
                                    <p className='text text-black font-m'>Fax:</p>
                                </div>
                                <div className="">
                                    <p className='text text-black pb-2px'>{FormatPhoneNumber(Data?.PracticeTelephone)}</p>
                                    <p className='text text-black'>{formatFax(Data?.PracticeFax)}</p>
                                </div>
                            </div>
                        </div>

                        {Data?.FormSelfPay ?
                            <div class="Form-content d-flex flex-column gap-3 pe-1 hover-scrollbar">
                                <div class="d-flex flex-column gap-3">
                                    <p class="heading fs-16 text-black">Insurance Policies</p>

                                    <div class="d-flex gap-6 align-items-center">
                                        <i class="icon-check fs-14"></i>
                                        <p class="text fs-14 text-black select-none">
                                            I do not have or do not want to use insurance benefits. I will be responsible for all charges related to the services rendered(SELF PAY).
                                        </p>
                                    </div>
                                </div>

                                <div class="d-flex flex-column gap-3">
                                    <div class="d-flex align-items-center gap-6 select-none cursor-pointer">
                                        <p class="heading fs-16 text-black">Acknowledgment</p>
                                    </div>

                                    <div class="d-flex flex-column gap-3">
                                        <div class="d-flex gap-6 align-items-baseline">
                                            <i class="icon-check fs-14"></i>
                                            <p class="text fs-14 text-black select-none">
                                                I have read and understood the contents of this form. All information entered by me is true and accurate, and I have only entered information about myself or an individual I am authorized to act on behalf of, such as my child. I consent to use electronic signatures, and my signature below is the same as a handwritten signature for the purposes of validity, enforceability, and admissibility.
                                            </p>
                                        </div>

                                        <div class="content-box">
                                            <div class="d-flex gap-10">
                                                <p class="text fs-14 min-w-156px text-end color-7070">
                                                    Signature
                                                </p>

                                                <div class="d-flex align-items-center justify-items-center signature-preview p-1">
                                                    <img src={imgFile} alt="Patient Signature" className="w-100" />
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div> :
                            <>
                                {Data?.TrueIdentifierCount > 0 ?
                                    <div className='ar-alert d-flex align-items-center gap-1 m-0'>
                                        <i className='icon-alert'></i>
                                        <p className='text'>There are {Data?.TrueIdentifierCount} fields that you didn't merge. Use the arrow icons or the <span className="heading cursor-pointer" onClick={ReconcileAll}>Reconcile All</span> option above to update the information.</p>
                                    </div>
                                    : null}

                                <div className="d-flex gap-10 scroll-content hover-scrollbar">
                                    <div className="submited-form-content p-2 w-50 h-100">
                                        <p className="heading fs-15 text-decoration-underline text-black pb-1-5">Submitted Form</p>
                                        <div className="d-flex flex-column gap-16">
                                            {/* Primary Insurance Section */}
                                            <div className="d-flex flex-column gap-16">
                                                <div className="d-flex align-items-center gap-6 w-max-content user-select-none cursor-pointer">
                                                    <i className="icon-up fs-9 black-icon"></i>
                                                    <p className="heading fs-16 text-black">Primary Insurance</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Insurance Name</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{Data?.PrimaryInsuranceName || ''}</p>
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Subscriber ID</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{Data?.PrimarySubscriberID || ''}</p>
                                                        {/* {getIdentifier('P_SubscriberID') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('PrimarySubscriberID')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Priority</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">
                                                            {/* {DropdownValueHandler(CoveragePriority, Data?.PrimaryCoveragePriority || '')}  */}
                                                            {Data?.PrimaryCoveragePriority}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Group #</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{Data?.PrimaryGroupNumber || ''}</p>
                                                        {/* {getIdentifier('P_GroupNumber') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('PrimaryGroupNumber')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Group Name</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{getField('PrimaryGroupName') || ''}</p>
                                                        {/* {getIdentifier('P_GroupName') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('PrimaryGroupName')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Relationship</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">
                                                            {/* {getField('PrimaryRelationship') || ''} */}
                                                            {DropdownValueHandler(Relationship, Data?.PrimaryRelationShipCode || '')}
                                                        </p>
                                                        {/* {getIdentifier('P_Relationship') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('PrimaryRelationship')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>First Name</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{getField('PrimaryFirstName') || ''}</p>
                                                        {/* {getIdentifier('P_FirstName') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('PrimaryFirstName')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Last Name</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{getField('PrimaryLastName') || ''}</p>
                                                        {/* {getIdentifier('P_LastName') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('PrimaryLastName')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Date of Birth</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{formatDate(getField('PrimaryDOB')) || ''}</p>
                                                        {/* {getIdentifier('P_DOB') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('PrimaryDOB')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Sex</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">
                                                            {/* {getField('PrimarySex') || ''} */}
                                                            {DropdownValueHandler(GenderDemographic, Data?.PriamrySex || '')}
                                                        </p>
                                                        {/* {getIdentifier('P_Gender') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('PrimarySex')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>SSN</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{getField('PrimarySSN') || ''}</p>
                                                        {/* {getIdentifier('P_SSN') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('PrimarySSN')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Address 1</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{getField('PrimaryAddress1') || ''}</p>
                                                        {/* {getIdentifier('P_Address1') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('PrimaryAddress1')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Address 2</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{getField('PrimaryAddress2') || ''}</p>
                                                        {/* {getIdentifier('P_Address2') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('PrimaryAddress2')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>City</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{getField('PrimaryCity') || ''}</p>
                                                        {/* {getIdentifier('P_City') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('PrimaryCity')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>State</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">
                                                            {/* {getField('PrimaryState') || ''} */}
                                                            {DropdownValueHandler(States, Data?.PriamryState || '')}
                                                        </p>
                                                        {/* {getIdentifier('P_State') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('PrimaryState')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>ZIP Code</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{getField('PrimaryZipCode') || ''}</p>
                                                        {/* {getIdentifier('P_Zip') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('PrimaryZipCode')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Front Side</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <ImagePreview item={{ file: Data?.PriamryInsuranceCardFront }} />
                                                        {/* <p className="text text-black fs-14 lh-14 text-decoration-underline">
                                                            {getField('PrimaryInsuranceCardFrontFileName') || ''}
                                                        </p> */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Back Side</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <ImagePreview item={{ file: Data?.PriamryInsuranceCardBack }} />
                                                        {/* <p className="text text-black fs-14 lh-14 text-decoration-underline">
                                                            {getField('PrimaryInsuranceCardBackFileName') || ''}
                                                        </p> */}
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Accept Assignment</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        {/* <p className="text text-black fs-14 lh-14">{Data?.PriamryAgreeAuthorization + ""}</p> */}

                                                        {Data?.PriamryAgreeAuthorization ?
                                                            <i className="icon-check fs-16 fw-900"></i>
                                                            :
                                                            <p className='text text-black fs-14 lh-14'>-</p>
                                                        }
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Secondary Insurance Section */}
                                            {/* {Data?.PatientSecondaryInsuranceID && ( */}
                                            <div className="d-flex flex-column gap-16">
                                                <div className="d-flex align-items-center gap-6 w-max-content user-select-none cursor-pointer">
                                                    <i className="icon-up fs-9 black-icon"></i>
                                                    <p className="heading fs-16 text-black">Secondary Insurance</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Insurance Name</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{Data?.SecondaryInsuranceName || ''}</p>
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Subscriber ID</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{Data?.SecondarySubscriberID || ''}</p>
                                                        {/* {getIdentifier('S_SubscriberID') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('SecondarySubscriberID')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Priority</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">
                                                            {/* {DropdownValueHandler(CoveragePriority, Data?.SecondaryCoveragePriority || '')} */}
                                                            {Data?.SecondaryCoveragePriority || ''}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Group #</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{Data?.SecondaryGroupNumber || ''}</p>
                                                        {/* {getIdentifier('S_GroupNumber') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('SecondaryGroupNumber')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Group Name</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{Data?.SecondaryGroupName || ''}</p>
                                                        {/* {getIdentifier('S_GroupName') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('SecondaryGroupName')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Relationship</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">
                                                            {/* {Data?.SecondaryRelationShipCode || ''} */}
                                                            {DropdownValueHandler(Relationship, Data?.SecondaryRelationShipCode || '')}
                                                        </p>
                                                        {/* {getIdentifier('S_Relationship') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('SecondaryRelationship')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>First Name</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{Data?.SecondaryFirstName || ''}</p>
                                                        {/* {getIdentifier('S_FirstName') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('SecondaryFirstName')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Last Name</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{Data?.SecondaryLastName || ''}</p>
                                                        {/* {getIdentifier('S_LastName') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('SecondaryLastName')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Date of Birth</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{formatDate(Data?.SecondaryDOB) || ''}</p>
                                                        {/* {getIdentifier('S_DOB') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('SecondaryDOB')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Sex</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">
                                                            {/* {Data?.SecondarySex || ''} */}
                                                            {DropdownValueHandler(GenderDemographic, Data?.SecondarySex || '')}
                                                        </p>
                                                        {/* {getIdentifier('S_Gender') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('SecondarySex')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>SSN</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{Data?.SecondarySSN || ''}</p>
                                                        {/* {getIdentifier('S_SSN') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('SecondarySSN')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Address 1</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{Data?.SecondaryAddress1 || ''}</p>
                                                        {/* {getIdentifier('S_Address1') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('SecondaryAddress1')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Address 2</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{Data?.SecondaryAddress2 || ''}</p>
                                                        {/* {getIdentifier('S_Address2') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('SecondaryAddress2')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>City</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{Data?.SecondaryCity || ''}</p>
                                                        {/* {getIdentifier('S_City') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('SecondaryCity')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>State</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">
                                                            {/* {Data?.SecondaryState || ''} */}
                                                            {DropdownValueHandler(States, Data?.SecondaryState || '')}
                                                        </p>
                                                        {/* {getIdentifier('S_State') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('SecondaryState')}></i> : null
                                                } */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>ZIP Code</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <p className="text text-black fs-14 lh-14">{Data?.SecondaryZipCode || ''}</p>
                                                        {/* {getIdentifier('S_Zip') === 'true' ?
                                                    <i className="icon-merge-top-right fs-10 cursor-pointer" onClick={() => ReconcileSingle('SecondaryZipCode')}></i> : null
                                                } */}
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Front Side</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <ImagePreview item={{ file: Data?.SecondaryInsuranceCardFront }} />
                                                        {/* <p className="text text-black fs-14 lh-14 text-decoration-underline">
                                                            {getField('SecondaryInsuranceCardFrontFileName') || ''}
                                                        </p> */}
                                                    </div>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Back Side</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        <ImagePreview item={{ file: Data?.SecondaryInsuranceCardBack }} />
                                                        {/* <p className="text text-black fs-14 lh-14 text-decoration-underline">
                                                            {getField('SecondaryInsuranceCardBackFileName') || ''}
                                                        </p> */}
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Accept Assignment</p>
                                                    <div className="d-flex align-items-center gap-15">
                                                        {Data?.SecondaryAgreeAuthorization ?
                                                            <i className="icon-check fs-16 fw-900"></i>
                                                            :
                                                            <p className='text text-black fs-14 lh-14'>-</p>
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                            {/* )} */}
                                            <div className="d-flex align-items-center gap-15">
                                                <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Acknowledgment</p>
                                                <div className="d-flex align-items-center gap-15">
                                                      {Data?.AcknowledgmentAccepted ?
                                                            <i className="icon-check fs-16 fw-900"></i>
                                                            :
                                                            <p className='text text-black fs-14 lh-14'>-</p>
                                                        }
                                                </div>
                                            </div>

                                            <div className="d-flex align-items-center gap-15">
                                                <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Signature</p>
                                                <div className="d-flex align-items-center gap-15 sign-content">
                                                    <img
                                                        src={imgFile}
                                                        style={{ maxHeight: "60px" }}
                                                        alt="Patient Signature"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Patient Insurance Section */}
                                    <div className="pateint-demograpic-content p-2 w-50 h-100">
                                        <p className="heading fs-15 text-decoration-underline text-black pb-1-5">Patient Insurance</p>
                                        <div className="d-flex flex-column gap-16">
                                            {/* Patient Primary Insurance */}
                                            <div className="d-flex flex-column gap-16">
                                                <div className="d-flex align-items-center gap-6 w-max-content user-select-none cursor-pointer">
                                                    <i className="icon-up fs-9 black-icon"></i>
                                                    <p className="heading fs-16 text-black">Primary Insurance</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Insurance Name</p>
                                                    <p className="text text-black fs-14 lh-14">{Data?.PatientPrimaryInsuranceName || ''}</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Subscriber ID</p>
                                                    <p className="text text-black fs-14 lh-14">{Data?.PatientPrimarySubscriberID || ''}</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Priority</p>
                                                    <p className="text text-black fs-14 lh-14">
                                                        {/* {Data?.PatientPrimaryCoveragePriority || ''} */}
                                                        {DropdownValueHandler(CoveragePriority, Data?.PatientPrimaryCoveragePriority || '')}
                                                    </p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Group #</p>
                                                    <p className="text text-black fs-14 lh-14">{Data?.PatientPrimaryGroupNumber || ''}</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Group Name</p>
                                                    <p className="text text-black fs-14 lh-14">{Data?.PatientPrimaryGroupName || ''}</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Relationship</p>
                                                    <p className="text text-black fs-14 lh-14">
                                                        {/* {Data?.PatientPrimaryRelationShipCode || ''} */}
                                                        {DropdownValueHandler(Relationship, Data?.PatientPrimaryRelationShipCode || '')}

                                                    </p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>First Name</p>
                                                    <p className="text text-black fs-14 lh-14">{Data?.PatientPrimaryFirstName || ''}</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Last Name</p>
                                                    <p className="text text-black fs-14 lh-14">{Data?.PatientPrimaryLastName || ''}</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Date of Birth</p>
                                                    <p className="text text-black fs-14 lh-14">{formatDate(Data?.PatientPrimaryDOB) || ''}</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Sex</p>
                                                    <p className="text text-black fs-14 lh-14">
                                                        {/* {Data?.PatientPrimaryGender || ''} */}
                                                        {DropdownValueHandler(GenderDemographic, Data?.PatientPrimaryGender || '')}
                                                    </p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>SSN</p>
                                                    <p className="text text-black fs-14 lh-14">{Data?.PatientPrimarySSN || ''}</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Address 1</p>
                                                    <p className="text text-black fs-14 lh-14">{Data?.PatientPrimaryAddressLine1 || ''}</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Address 2</p>
                                                    <p className="text text-black fs-14 lh-14">{Data?.PatientPrimaryAddressLine2 || ''}</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>City</p>
                                                    <p className="text text-black fs-14 lh-14">{Data?.PatientPrimaryCity || ''}</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>State</p>
                                                    <p className="text text-black fs-14 lh-14">
                                                        {/* {Data?.PatientPrimamryState || Data?.PatientPrimaryState || ''} */}
                                                        {DropdownValueHandler(States, Data?.PatientPrimamryState || '')}
                                                    </p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>ZIP Code</p>
                                                    <p className="text text-black fs-14 lh-14">{Data?.PatientPrimaryZipCode || ''}</p>
                                                </div>
                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Front Side</p>
                                                    {/* <p className="text text-black fs-14 lh-14">{Data?.PatientPriamryInsuranceCardFront || ''}</p> */}
                                                    <ImagePreview item={{ file: Data?.PatientPriamryInsuranceCardFront }} />
                                                </div>
                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Back Side</p>
                                                    {/* <p className="text text-black fs-14 lh-14">{Data?.PatientPriamryInsuranceCardBack || ''}</p> */}
                                                    <ImagePreview item={{ file: Data?.PatientPriamryInsuranceCardBack }} />
                                                </div>
                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Accept Assignment</p>

                                                    {Data?.PatientPriamryAcceptAssignment ?
                                                        <i className="icon-check fs-16 fw-900"></i>
                                                        :
                                                        <p className='text text-black fs-14 lh-14'>-</p>
                                                    }
                                                    {/* <p className="text text-black fs-14 lh-14">{Data?.PatientPriamryAcceptAssignment ? Data?.PatientPriamryAcceptAssignment + "" : ''}</p> */}
                                                </div>
                                            </div>

                                            {/* Patient Secondary Insurance - Only show if exists */}
                                            {/* {Data?.PatientSecondaryInsuranceID && ( */}
                                            <div className="d-flex flex-column gap-16">
                                                <div className="d-flex align-items-center gap-6 w-max-content user-select-none cursor-pointer">
                                                    <i className="icon-up fs-9 black-icon"></i>
                                                    <p className="heading fs-16 text-black">Secondary Insurance</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Insurance Name</p>
                                                    <p className="text text-black fs-14 lh-14">{Data?.PatientSecondaryInsuranceName || ''}</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Subscriber ID</p>
                                                    <p className="text text-black fs-14 lh-14">{Data?.PatientSecondarySubscriberID || ''}</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Priority</p>
                                                    <p className="text text-black fs-14 lh-14">
                                                        {/* {Data?.PatientSecondaryCoveragePriority || ''} */}
                                                        {DropdownValueHandler(CoveragePriority, Data?.PatientSecondaryCoveragePriority || '')}
                                                    </p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Group #</p>
                                                    <p className="text text-black fs-14 lh-14">{Data?.PatientSecondaryGroupNumber || ''}</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Group Name</p>
                                                    <p className="text text-black fs-14 lh-14">{Data?.PatientSecondaryGroupName || ''}</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Relationship</p>
                                                    <p className="text text-black fs-14 lh-14">
                                                        {/* {Data?.PatientSecondaryRelationShipCode || ''} */}
                                                        {DropdownValueHandler(Relationship, Data?.PatientSecondaryRelationShipCode || '')}
                                                    </p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>First Name</p>
                                                    <p className="text text-black fs-14 lh-14">{Data?.PatientSecondaryFirstName || ''}</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Last Name</p>
                                                    <p className="text text-black fs-14 lh-14">{Data?.PatientSecondaryLastName || ''}</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Date of Birth</p>
                                                    <p className="text text-black fs-14 lh-14">{formatDate(Data?.PatientSecondaryDOB) || ''}</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Sex</p>
                                                    <p className="text text-black fs-14 lh-14">
                                                        {/* {Data?.PatientSecondaryGender || ''} */}
                                                        {DropdownValueHandler(GenderDemographic, Data?.PatientSecondaryGender || '')}
                                                    </p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>SSN</p>
                                                    <p className="text text-black fs-14 lh-14">{Data?.PatientSecondarySSN || ''}</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Address 1</p>
                                                    <p className="text text-black fs-14 lh-14">{Data?.PatientSecondaryAddressLine1 || ''}</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Address 2</p>
                                                    <p className="text text-black fs-14 lh-14">{Data?.PatientSecondaryAddressLine2 || ''}</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>City</p>
                                                    <p className="text text-black fs-14 lh-14">{Data?.PatientSecondaryCity || ''}</p>
                                                </div>

                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>State</p>
                                                    <p className="text text-black fs-14 lh-14">
                                                        {DropdownValueHandler(States, Data?.PatientSecondaryState || '')}
                                                        {/* {Data?.PatientSecondaryState || ''} */}
                                                    </p>
                                                </div>
                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>ZIP Code</p>
                                                    <p className="text text-black fs-14 lh-14">{Data?.PatientSecondaryZipCode || ''}</p>
                                                </div>
                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Front Side</p>
                                                    {/* <p className="text text-black fs-14 lh-14">{Data?.PatientSecondaryInsuranceCardFront || ''}</p> */}
                                                    <ImagePreview item={{ file: Data?.PatientSecondaryInsuranceCardFront }} />
                                                </div>
                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Back Side</p>
                                                    {/* <p className="text text-black fs-14 lh-14">{Data?.PatientSecondaryInsuranceCardBack || ''}</p> */}
                                                    <ImagePreview item={{ file: Data?.PatientSecondaryInsuranceCardBack }} />
                                                </div>
                                                <div className="d-flex align-items-center gap-15">
                                                    <p className='text fs-14 text-black min-w-156px text-end ar-light-color'>Accept Assignment</p>

                                                    {Data?.PatientSecondaryAcceptAssignment ?
                                                        <i className="icon-check fs-16 fw-900"></i>
                                                        :
                                                        <p className='text text-black fs-14 lh-14'>-</p>
                                                    }
                                                    {/* <p className="text text-black fs-14 lh-14">{Data?.PatientSecondaryAcceptAssignment ? Data?.PatientSecondaryAcceptAssignment + "" : ''}</p> */}
                                                </div>
                                            </div>

                                            {/* )} */}
                                        </div>
                                    </div>
                                </div>
                            </>}



                    </div>
                </div>
            </Modal.Body>
        </Modal>
    )
}

export default MergeInsuranceForm